(ns bp-clients.tv-app.common.events
  "Namespace for common events shared across the application.
  Mainly used to access browser/web standard api features via events"
  (:require
   [bp-clients.tv-app.api.events :as api]
   [bp-clients.tv-app.api.graphql.license :as license-queries]
   [bp-clients.tv-app.api.graphql.player :as player-queries]
   [bp-clients.tv-app.utils.indexdb :as idb]
   [bp-clients.tv-app.utils.keycodes :as keycodes]
   [bp-clients.tv-app.utils.local-storage :as ls]
   [bp-clients.tv-app.utils.logger :as logger]
   [bp-clients.tv-app.utils.utils :as utils]
   [bp-clients.tv-app.utils.window :as window]
   [re-frame.core :refer [reg-fx reg-event-fx reg-event-db dispatch path dispatch-sync]])
  (:import [goog.events EventType]))

(def common-path ::common)

(def common-interceptors
  "Checks spec and selects nav path interceptor"
  [(path common-path)])

(defn- handle-error-ev
  "Error handler entrypoint"
  [{:keys [db]} [_event error]]
  {:db (update db :error-queue conj error)})

(defn- process-error-queue-ev
  "Process error events queue"
  [{:keys [db]} _]
  (let [error-queue (->> db
                         :error-queue
                         (map :retry-ev)
                         (mapv #(list :dispatch %)))]
    {:fx error-queue
     :db (assoc db :error-queue [])}))

(defn local-storage-fx
  "Local storage side effects"
  [{:keys [key value]}]
  (if value
    (ls/set-item! key value)
    (ls/remove-item! key)))

(defn local-storage-ev-fx
  "Dispatches the ::local-storage side effect"
  [{:keys [db]} [_ key value]]
  {:db db
   ::local-storage {:key key
                    :value value}})

(defn endpoint-request-error-ev-fx
  "Given the app's state and a reponse error. Stores the request failure on the state."
  [{:keys [db]} [event-name request-type response]]
  {:db (assoc-in
        db
        [::last-http-error request-type]
        (merge {:event-name event-name}
               (select-keys response [:last-error :status-text])))})

(defonce interval-handler
  (let [live-intervals (atom {})]
    (fn handler [{:keys [action id frequency event]}]
      (condp = action
        :clean   (doall
                  (map #(handler {:action :end  :id  %1}) (keys @live-intervals)))
        :start       (swap! live-intervals assoc id (window/set-interval #(dispatch event) frequency))
        :end     (do (window/clear-interval (get @live-intervals id))
                     (swap! live-intervals dissoc id))))))

;; when this code is reloaded `:clean` existing intervals
(interval-handler {:action :clean})

(defn open-player-ev
  [{:keys [db]} [_event-name media active-item]]
  (let [query {:media-id (:id media)
               :playlist-id (:slug active-item)
               :hdcp false}
        default-config {}
        player {:playing? true
                :loading? true
                :config default-config}]
    {:db (-> db
             (assoc :player player)
             (assoc-in [:player :media] media)
             (assoc-in [:player :media :playlist] active-item))
     :dispatch-n [[::fetch-media-playback query]
                  [:bp-clients.tv-app.analytics.events/screen-requested {:view :player
                                                                         :item-id (str (:slug active-item) "/" (:id media))}]]}))

(defn close-player-ev
  [{:keys [db]}]
  {:db (dissoc db :player)
   :dispatch [:bp-clients.tv-app.nav.events/keep-watching]})

(defn fetch-media-for-playback
  "Fetches media information for playback."
  [{:keys [db]} [_event-name media]]
  {:db       db
   :dispatch [::api/query-graphql
              (merge {:instance-id :auth
                      :on-success  ::fetch-media-playback-success
                      :on-error    [::fetch-media-playback-error media]}
                     (license-queries/media-query media))]})

(defn fetch-media-for-playback-success
  "Success event handler for the ::fetch-login-token"
  [{:keys [db]} [_event-name {:keys [decoder data]}]]
  (let [playback-data (decoder data)
        language-data (-> data :user :settings :player :language)]
    {:db (-> db
             (assoc-in [:player :loading?] false)
             (assoc-in [:player :show-controls?] true)
             (assoc-in [:player :media :drm] playback-data)
             (assoc-in [:player :controls :last-checkpoint] (:checkpoint playback-data))
             (assoc-in [:player :controls :subtitle-track-selected] (:subtitles language-data))
             (assoc-in [:player :controls :audio-track-selected] (:audio language-data)))}))

(defn fetch-media-for-playback-error
  [{:keys [db]} [_event-name media {:keys [error-type]}]]
  {:db         db
   :dispatch [::handle-error {:type     (cond
                                          (:network-offline? db)   :connection
                                          (-> error-type nil? not) error-type
                                          :else                    :unexpected)
                              :retry-ev [::fetch-media-playback media]}]})

(defn update-checkpoint
  "Updates checkpoint in a given media/playlist"
  [{:keys [db]} [_event-name checkpoint]]
  {:db       db
   :dispatch [::api/mutate-graphql
              (merge {:instance-id :auth
                      :on-success  ::update-checkpoint-success
                      :on-error    [::update-checkpoint-error]}
                     (player-queries/update-checkpoint-mutation checkpoint))]})

(defn update-checkpoint-success
  "Success event handler for the ::update-checkpoint"
  [{:keys [db]} [_event-name _]]
  {:db  db})

(defn update-checkpoint-error
  [{:keys [db]} [_event-name _error]]
  {:db         db})

(def ^:private show-player-controls-timeout (atom 0))

(defn show-player-controls
  [{:keys [db]} [_event-name show?]]
  (when show? ;; hide controls when paused with (and show? (-> db :player :controls paused?))
    (js/clearTimeout @show-player-controls-timeout)
    (reset! show-player-controls-timeout (js/setTimeout #(dispatch [::show-player-controls false]) 5000)))
  {:db (cond-> db
         (not show?) (assoc-in [:player :controls :track-selector-open?] false) ;; hide track selector when controls become hidden
         true (assoc-in [:player :show-controls?] (true? show?)))
   :dispatch [::show-rating (not show?)]})

(defn show-rating
  [db [_event-name show?]]
  (when show?
    (js/setTimeout #(dispatch [::show-rating false]) 5000))
  (assoc-in db [:player :show-rating?] (true? show?)))

(def ^:private checkpoint-frequency-seconds
  30)

(defn- on-stalled []
  (logger/critical "Player stalled"))

(defn- on-abort []
  (logger/info "Player aborted"))

(defn- on-suspend []
  (logger/critical "Player data loading has been suspended"))

(defn player-update
  [{:keys [db]} [_event-name {:keys [paused? current-time type]}]]
  (let [controls (-> db :player :controls)]
    (condp = type
      EventType/STALLED    (do (on-stalled)
                               {:db db})
      EventType/ABORT      (do (on-abort)
                               {:db db})
      EventType/SUSPEND    (do (on-suspend)
                               {:db db})
      EventType/TIMEUPDATE (let [db              (assoc-in db [:player :controls :current-time] current-time)
                                 last-checkpoint (:last-checkpoint controls)
                                 elapsed-time    (- current-time last-checkpoint)]
                             (if (>= (clojure.core/abs elapsed-time) checkpoint-frequency-seconds)
                               {:db       (assoc-in db [:player :controls :last-checkpoint] current-time)
                                :dispatch [::update-checkpoint {:media-id    (-> db :player :media :id)
                                                                :playlist-id (-> db :player :media :drm :playlist :id)
                                                                :checkpoint  current-time}]}
                               {:db db}))
      EventType/WAITING    {:db (assoc-in db [:player :can-play?] false)}
      EventType/PAUSE      {:db       (assoc-in db [:player :controls :paused?] true)
                            :dispatch [::show-player-controls true]}
      EventType/PLAY       {:db (assoc-in db [:player :controls :paused?] false)}
      EventType/PLAYING    {:db       db
                            :dispatch [::show-player-controls true]}
      EventType/CANPLAY    {:db       (assoc-in db [:player :can-play?] true)
                            :dispatch [::update-checkpoint {:media-id    (-> db :player :media :id)
                                                            :playlist-id (-> db :player :media :drm :playlist :id)
                                                            :checkpoint  current-time}]}
      "PLAY/PAUSE"         {:db (assoc-in db [:player :controls :paused?] (not paused?))}
      {:db db})))

(defn set-media-info
  [db [_event-name {:keys [audio-tracks subtitle-tracks variants texts] :as media-info}]]
  (let [audio-track-selected    (get-in db [:player :controls :audio-track-selected])
        active-audio            (if (= audio-track-selected "original")
                                  (first (filterv #(= "main" (:role %)) audio-tracks))
                                  (first (filterv #(utils/insensitive-compare audio-track-selected (:language %))
                                                  variants)))
        fallback-audio          (when-not active-audio
                                  (logger/warn "Active audio not found for this media."
                                               (assoc media-info
                                                      :audio-track-selected audio-track-selected))
                                  (first (filterv :active variants)))
        subtitle-track-selected (get-in db [:player :controls :subtitle-track-selected])
        active-subtitle         (first (filterv #(utils/insensitive-compare subtitle-track-selected (:language %))
                                                texts))
        fallback-text           (when-not active-subtitle
                                  (logger/warn "Active subtitle not found for this media."
                                               (assoc media-info
                                                      :subtitle-track-selected subtitle-track-selected))
                                  (or (first (filterv :active texts))
                                      {:language "none"}))]
    (-> db
        (assoc-in [:player :controls :audio-tracks] audio-tracks)
        (assoc-in [:player :controls :subtitle-tracks] subtitle-tracks)
        (assoc-in [:player :controls :audio-track-selected] (:language (or active-audio fallback-audio)))
        (assoc-in [:player :controls :subtitle-track-selected] (:language (or active-subtitle fallback-text))))))

(defn toggle-track-selector
  [db [_event-name]]
  (update-in db [:player :controls :track-selector-open?] #(-> % true? not)))

(defn select-audio-track
  [{:keys [db]} [_event-name audio-track]]
  (let [subtitles-track (get-in db [:player :controls :subtitle-track-selected])]
    {:db       (assoc-in db [:player :controls :audio-track-selected] audio-track)
     :dispatch [::api/mutate-graphql
                (merge {:instance-id :auth
                        :on-success  ::update-user-settings-success
                        :on-error    [::update-user-settings-error]}
                       (player-queries/update-user-settings-mutation {:audio     audio-track
                                                                      :subtitles subtitles-track}))]}))

(defn select-subtitle-track
  [{:keys [db]} [_event-name subtitles-track]]
  (let [audio-track (get-in db [:player :controls :audio-track-selected])]
    {:db       (assoc-in db [:player :controls :subtitle-track-selected] subtitles-track)
     :dispatch [::api/mutate-graphql
                (merge {:instance-id :auth
                        :on-success  ::update-user-settings-success
                        :on-error    [::update-user-settings-error]}
                       (player-queries/update-user-settings-mutation {:audio audio-track
                                                                      :subtitles subtitles-track}))]}))

(defn update-user-settings-success
  [{:keys [db]} _]
  db)

(defn update-user-settings-error
  [{:keys [db]} _]
  db)

(defonce *user-database (atom nil))
(def database-name "indexed.db.tv")
(def user-table "user")
(def user-api-key "token")
(def database-version 1)

(defn create-user-db
  [{:keys [success]}]
  (idb/create-db
   *user-database
   #(dispatch-sync success)
   {:db/name database-name
    :db/version database-version
    :db/table user-table}))

(defn indexdb-put
  "Local storage side effects"
  [{:keys [table column value success]}]
  (when @*user-database
    (->
     (idb/store @*user-database table "readwrite")
     (idb/put (clj->js value) column)
     (idb/on "success" #(when success (dispatch-sync [success]))))))

(defn indexdb-get
  "Local storage side effects"
  [{:keys [table column success init?]}]
  (when @*user-database
    (->
     (idb/store @*user-database table)
     (idb/get-value (clj->js column))
     (idb/on "success" #(when success (dispatch-sync (cond-> [success]
                                                       init? (merge {})
                                                       true  (merge (idb/result %)))))))))

(defn indexdb
  [{:keys [operation] :as config}]
  (condp = operation
    :put (indexdb-put config)
    (indexdb-get config)))

(defn init-indexdb
  [db [_event-name success-event]]
  (create-user-db {:success success-event})
  db)

(defn play-ev
  [db _]
  (when-let [video-el (js/document.querySelector "video")]
    (.play video-el))
  db)

(defn pause-ev
  [db _]
  (when-let [video-el (js/document.querySelector "video")]
    (.pause video-el))
  db)

(defn play-pause-ev
  [db _]
  (when-let [video-el (js/document.querySelector "video")]
    (if (.-paused video-el)
      (.play video-el)
      (.pause video-el)))
  db)

(defn click-ev
  [db _]
  (let [is-button? (= "BUTTON" (.-nodeName js/document.activeElement))]
    (when-not is-button?
      (.click js/document.activeElement)))
  db)

(defn- handle-playback-key
  "Handles playback keypresses"
  [{:keys [db]} [_event-name {:keys [keyCode]}]]
  (cond
    (keycodes/play? keyCode)       (play-ev db nil)
    (keycodes/pause? keyCode)      (pause-ev db nil)
    (keycodes/play-pause? keyCode) (play-pause-ev db nil))
  {:db db})

(defn- welcome-modal-showed-ev
  [db [_ welcome-modal-showed?]]
  (assoc db :welcome-modal-showed? welcome-modal-showed?))

(reg-fx ::interval interval-handler)
(reg-fx ::local-storage local-storage-fx)
(reg-fx ::indexdb indexdb)
(reg-event-fx ::local-storage local-storage-ev-fx)
(reg-event-fx ::process-error-queue common-interceptors process-error-queue-ev)
(reg-event-fx ::handle-error common-interceptors handle-error-ev)
(reg-event-fx ::endpoint-request-error common-interceptors endpoint-request-error-ev-fx)
(reg-event-fx ::open-player common-interceptors open-player-ev)
(reg-event-fx ::close-player common-interceptors close-player-ev)
(reg-event-fx ::show-player-controls common-interceptors show-player-controls)
(reg-event-db ::show-rating common-interceptors show-rating)
(reg-event-fx ::player-update common-interceptors player-update)
(reg-event-fx ::fetch-media-playback common-interceptors fetch-media-for-playback)
(reg-event-fx ::fetch-media-playback-success common-interceptors fetch-media-for-playback-success)
(reg-event-fx ::fetch-media-playback-error common-interceptors fetch-media-for-playback-error)
(reg-event-fx ::update-checkpoint common-interceptors update-checkpoint)
(reg-event-fx ::update-checkpoint-success common-interceptors update-checkpoint-success)
(reg-event-fx ::update-checkpoint-error common-interceptors update-checkpoint-error)
(reg-event-db ::set-media-info common-interceptors set-media-info)
(reg-event-db ::toggle-track-selector common-interceptors toggle-track-selector)
(reg-event-fx ::select-audio-track common-interceptors select-audio-track)
(reg-event-fx ::select-subtitle-track common-interceptors select-subtitle-track)
(reg-event-fx ::update-user-settings-error common-interceptors update-user-settings-error)
(reg-event-fx ::update-user-settings-success common-interceptors update-user-settings-success)
(reg-event-db ::init-indexdb common-interceptors init-indexdb)
(reg-event-fx ::handle-playback-key common-interceptors handle-playback-key)
(reg-event-db ::welcome-modal-showed common-interceptors welcome-modal-showed-ev)
(reg-event-db ::pause pause-ev)
(reg-event-db ::play play-ev)
(reg-event-db ::play-pause play-pause-ev)
(reg-event-db ::click click-ev)
